import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, onSnapshot, orderBy, updateDoc, deleteDoc, doc, setDoc, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, MousePointerClick, Link2, Trash2, RotateCcw,
  UserPlus, Search, RefreshCw, Calendar, Building2, Phone, Mail, UserRound,
  ChevronDown, ChevronUp, ClipboardCopy, CheckCircle, XCircle, LogIn, LogOut, ShieldAlert
} from 'lucide-react';
import { SUPER_ADMIN_EMAIL } from '../../../../constants';

const normalize = (value) => (value || '').trim().toLowerCase();

const slugify = (text) => {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const toDate = (ts) => {
  if (!ts) return null;
  try {
    if (ts.toDate) return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date(ts);
  } catch {
    return null;
  }
};

const formatTimestamp = (ts) => {
  const d = toDate(ts);
  if (!d || isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatCount = (value) => {
  const n = Number(value) || 0;
  if (n < 1000) return String(n);
  if (n < 1000000) {
    const k = n / 1000;
    return `${k % 1 === 0 ? k : k.toFixed(1)}k`;
  }
  const m = n / 1000000;
  return `${m % 1 === 0 ? m : m.toFixed(1)}M`;
};

const sortByDateDesc = (items) => {
  return [...items].sort((a, b) => {
    const ta = toDate(a.createdAt)?.getTime() || 0;
    const tb = toDate(b.createdAt)?.getTime() || 0;
    return tb - ta;
  });
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const startOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const isWithinPeriod = (ts, period) => {
  const d = toDate(ts);
  if (!d || isNaN(d.getTime())) return false;
  if (period === 'today') return d >= startOfToday();
  if (period === 'month') return d >= startOfThisMonth();
  return true;
};

const periodLabel = (period) => {
  if (period === 'today') return 'today';
  if (period === 'month') {
    return new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }
  return 'all time';
};

const groupUsersByStay = (users, fallbackName) => {
  const map = new Map();
  users.forEach((u) => {
    const stay = (u.itemTitle || fallbackName || '').trim();
    const key = stay || 'Unknown Stay';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return [...map.entries()]
    .map(([stay, count]) => ({ stay, count }))
    .sort((a, b) => b.count - a.count || a.stay.localeCompare(b.stay));
};

const ENQUIRY_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'month', label: 'This Month' },
  { value: 'all', label: 'All Time' }
];

const InfluencerCommission = () => {
  const navigate = useNavigate();

  const [influencers, setInfluencers] = useState([]);
  const [creatorLinks, setCreatorLinks] = useState([]);
  const [joinedUsers, setJoinedUsers] = useState([]);
  const [resortBookings, setResortBookings] = useState([]);
  const [bookingAttributions, setBookingAttributions] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [guestContacts, setGuestContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [emailInput, setEmailInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [enquiryFilter, setEnquiryFilter] = useState('month');

  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [influencersLoaded, setInfluencersLoaded] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [togglingTracking, setTogglingTracking] = useState(false);
  const [togglingAccessId, setTogglingAccessId] = useState(null);

  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      setAuthChecked(true);
    });
    return () => unsubAuth();
  }, []);

  const isSuperAdmin = user ? normalize(user.email || '') === normalize(SUPER_ADMIN_EMAIL) : false;

  const currentInfluencerAccess = useMemo(() => {
    if (!user) return false;
    const email = normalize(user.email || '');
    const record = influencers.find((i) => normalize(i.email || i.id) === email);
    return !!record && record.accessEnabled !== false;
  }, [user, influencers]);

  const authorizedEmails = useMemo(
    () => new Set(influencers.map((i) => normalize(i.email || i.id)).filter(Boolean)),
    [influencers]
  );

  const isAuthorizedInfluencer = user
    ? authorizedEmails.has(normalize(user.email || '')) && currentInfluencerAccess
    : false;
  const canAccess = isSuperAdmin || isAuthorizedInfluencer;

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        console.error('Login error:', err);
        setError('Sign-in failed: ' + (err.message || 'Please try again.'));
      }
    }
  };

  const handleSignOut = () => {
    signOut(auth).catch((err) => console.error('Sign out error:', err));
  };

  const loadResortBookings = useCallback(() => {
    getDocs(collection(db, 'resortBookings'))
      .then((snap) => {
        setResortBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLastUpdated(new Date());
      })
      .catch((err) => {
        console.error('Error loading resort bookings:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const unsubSettings = onSnapshot(
      doc(db, 'settings', 'influencerTracking'),
      (snap) => {
        setTrackingEnabled(snap.exists() ? snap.data().enabled !== false : true);
        setSettingsLoaded(true);
      },
      (err) => {
        console.error('Error loading access setting:', err);
        setTrackingEnabled(true);
        setSettingsLoaded(true);
      }
    );
    return () => unsubSettings();
  }, []);

  useEffect(() => {
    const unsubInfluencers = onSnapshot(
      query(collection(db, 'influencers')),
      (snap) => {
        setInfluencers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setInfluencersLoaded(true);
      },
      (err) => {
        console.error('Error loading influencers:', err);
        setError('Failed to load influencers: ' + err.message);
      }
    );

    const unsubLinks = onSnapshot(
      collection(db, 'creatorLinks'),
      (snap) => {
        setCreatorLinks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error('Error loading creator links:', err)
    );

    // Stored, frozen booking attributions (written at booking time).
    const unsubAttributions = onSnapshot(
      collection(db, 'bookingAttributions'),
      (snap) => {
        setBookingAttributions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error('Error loading booking attributions:', err)
    );

    let unsubUsers = null;
    const usersRef = collection(db, 'FromInfluencers');
    try {
      unsubUsers = onSnapshot(
        query(usersRef, orderBy('createdAt', 'desc')),
        (snap) => {
          setJoinedUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        () => {
          unsubUsers = onSnapshot(collection(db, 'FromInfluencers'), (snap) => {
            setJoinedUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          }, (err2) => console.error('Error loading joined users:', err2));
        }
      );
    } catch (err) {
      console.error('Error subscribing to joined users:', err);
    }

    loadResortBookings();

    return () => {
      unsubInfluencers();
      unsubLinks();
      unsubAttributions();
      if (unsubUsers) unsubUsers();
    };
  }, [refreshTick, loadResortBookings]);

  const emailKey = useMemo(() => {
    const set = new Set();
    influencers.forEach((i) => {
      const e = normalize(i.email || i.id);
      if (e) set.add(e);
    });
    creatorLinks.forEach((l) => {
      const e = normalize(l.influencerEmail);
      if (e) set.add(e);
    });
    return [...set].sort().join('|');
  }, [influencers, creatorLinks]);

  useEffect(() => {
    const emails = emailKey ? emailKey.split('|') : [];
    if (emails.length === 0) return;
    const chunks = [];
    for (let i = 0; i < emails.length; i += 10) chunks.push(emails.slice(i, i + 10));
    let cancelled = false;
    Promise.all(chunks.map((chunk) =>
      getDocs(query(collection(db, 'users'), where('email', 'in', chunk)))
        .then((snap) => {
          const byEmail = {};
          snap.docs.forEach((d) => {
            const e = normalize(d.data().email);
            if (e) byEmail[e] = d.data().displayName || d.data().name || '';
          });
          return byEmail;
        })
        .catch(() => ({}))
    )).then((results) => {
      if (cancelled) return;
      const merged = {};
      results.forEach((r) => Object.assign(merged, r));
      setNameMap({ ...merged });
    });
    return () => { cancelled = true; };
  }, [emailKey]);

  const usersByToken = useMemo(() => {
    const map = new Map();
    joinedUsers.forEach((u) => {
      if (!u.ref) return;
      if (!map.has(u.ref)) map.set(u.ref, []);
      map.get(u.ref).push(u);
    });
    return map;
  }, [joinedUsers]);

  const tokenToInfluencerEmail = useMemo(() => {
    const map = new Map();
    creatorLinks.forEach((l) => {
      if (l.token) map.set(l.token, normalize(l.influencerEmail));
    });
    return map;
  }, [creatorLinks]);

  const guestReferredBy = useMemo(() => {
    const map = new Map();
    joinedUsers.forEach((u) => {
      const token = u.ref;
      if (!token) return;
      const inf = tokenToInfluencerEmail.get(token);
      if (!inf) return;
      const guestEmail = normalize(u.email || u.userEmail || (u.linkData && u.linkData.email));
      if (!guestEmail) return;
      if (!map.has(guestEmail)) map.set(guestEmail, new Set());
      map.get(guestEmail).add(inf);
    });
    return map;
  }, [joinedUsers, tokenToInfluencerEmail]);

  // Frozen attributions persisted in the `bookingAttributions` collection at
  // booking time: bookingId -> Set<influencerEmail>. This is the source of truth.
  const storedAttributionByBooking = useMemo(() => {
    const map = new Map();
    bookingAttributions.forEach((a) => {
      if (a.attributionStatus === 'cancelled') return;
      const bookingId = a.bookingId;
      const inf = normalize(a.influencerEmail);
      if (!bookingId || !inf) return;
      if (!map.has(bookingId)) map.set(bookingId, new Set());
      map.get(bookingId).add(inf);
    });
    return map;
  }, [bookingAttributions]);

  // Returns the set of influencer emails a booking is attributed to via a creator token.
  // Prefers the stored attribution; falls back to live matching for legacy bookings.
  // Bookings with no creator token are considered direct bookings.
  const bookingCreatorEmails = useCallback((booking) => {
    const stored = storedAttributionByBooking.get(booking.id);
    if (stored && stored.size > 0) return stored;

    const found = new Set();
    const explicitToken =
      booking.ref ||
      booking.creatorToken ||
      booking.token ||
      (booking.commissionAllocations && booking.commissionAllocations.creatorToken);
    if (explicitToken) {
      const inf = tokenToInfluencerEmail.get(explicitToken);
      if (inf) found.add(inf);
    }
    const guest = normalize(booking.userEmail || booking.email);
    if (guest && guestReferredBy.has(guest)) {
      guestReferredBy.get(guest).forEach((e) => found.add(e));
    }
    return found;
  }, [storedAttributionByBooking, tokenToInfluencerEmail, guestReferredBy]);

  const influencerCards = useMemo(() => {
    const byEmail = new Map();
    influencers.forEach((inf) => {
      const email = normalize(inf.email || inf.id);
      if (!email || byEmail.has(email)) return;
      const rawEmail = inf.email || inf.id;

      const links = creatorLinks.filter((l) => normalize(l.influencerEmail) === email);

      const usersJoined = links.flatMap((l) => usersByToken.get(l.token) || []);
      const seenIds = new Set(usersJoined.map((u) => u.id));
      joinedUsers.forEach((u) => {
        if (u.linkData && typeof u.linkData === 'object' && normalize(u.linkData.influencerEmail) === email) {
          if (!seenIds.has(u.id)) {
            usersJoined.push(u);
            seenIds.add(u.id);
          }
        }
      });

      const enquiries = resortBookings
        .filter((b) => bookingCreatorEmails(b).has(email))
        .map((b) => ({
          id: b.id,
          property: b.resortName || b.propertyName || 'Unknown Property',
          createdAt: b.createdAt,
          userEmail: b.userEmail || '',
          guests: b.guests || '',
          checkIn: b.checkIn || '',
          checkOut: b.checkOut || ''
        }));

      const totalClicks = links.reduce((sum, l) => sum + (l.clicks || 0), 0);

      byEmail.set(email, {
        id: inf.id,
        email: rawEmail,
        name: nameMap[email] || rawEmail.split('@')[0] || 'Influencer',
        accessEnabled: inf.accessEnabled !== false,
        links,
        usersJoined: sortByDateDesc(usersJoined),
        enquiries: sortByDateDesc(enquiries),
        totalClicks,
        totalLeads: usersJoined.length + enquiries.length
      });
    });
    return Array.from(byEmail.values()).sort((a, b) =>
      b.totalClicks - a.totalClicks ||
      b.totalLeads - a.totalLeads ||
      a.email.localeCompare(b.email)
    );
  }, [influencers, creatorLinks, usersByToken, joinedUsers, resortBookings, bookingCreatorEmails, nameMap]);

  const unassignedLinks = useMemo(() => {
    const authorized = new Set(influencerCards.map((c) => normalize(c.email)));
    return creatorLinks.filter((l) => !authorized.has(normalize(l.influencerEmail)));
  }, [creatorLinks, influencerCards]);

  // Direct bookings: records stored in `bookingAttributions` with no influencer
  // (i.e. no creator token), plus any legacy resort booking not yet recorded.
  const directBookings = useMemo(() => {
    const seen = new Set();

    const fromCollection = bookingAttributions
      .filter((a) => a.attributionStatus !== 'cancelled' && !normalize(a.influencerEmail))
      .filter((a) => isWithinPeriod(a.createdAt, enquiryFilter))
      .map((a) => {
        const id = a.bookingId || a.id;
        seen.add(id);
        return {
          id,
          property: a.propertyName || 'Unknown Property',
          guestEmail: a.guestEmail || '',
          createdAt: a.createdAt,
          guests: a.guests || '',
          rooms: a.rooms || '',
          checkIn: a.checkIn || '',
          checkOut: a.checkOut || '',
          totalPrice: a.bookingAmount ?? 0,
          status: a.status || 'pending'
        };
      });

    const legacy = resortBookings
      .filter((b) => !seen.has(b.id))
      .filter((b) => isWithinPeriod(b.createdAt, enquiryFilter))
      .filter((b) => bookingCreatorEmails(b).size === 0)
      .map((b) => ({
        id: b.id,
        property: b.resortName || b.propertyName || 'Unknown Property',
        guestEmail: b.userEmail || b.email || '',
        createdAt: b.createdAt,
        guests: b.guests || '',
        rooms: b.rooms || '',
        checkIn: b.checkIn || '',
        checkOut: b.checkOut || '',
        totalPrice: b.finalPrice ?? b.totalPrice ?? 0,
        status: b.status || 'pending'
      }));

    return sortByDateDesc([...fromCollection, ...legacy]);
  }, [bookingAttributions, resortBookings, bookingCreatorEmails, enquiryFilter]);

  // Unique guest emails appearing in the direct bookings list, used to enrich
  // each card with the guest's name and phone from their user profile.
  const directGuestEmails = useMemo(() => {
    const set = new Set();
    directBookings.forEach((b) => {
      const e = normalize(b.guestEmail);
      if (e) set.add(e);
    });
    return [...set].sort();
  }, [directBookings]);

  useEffect(() => {
    if (directGuestEmails.length === 0) return;
    const chunks = [];
    for (let i = 0; i < directGuestEmails.length; i += 10) chunks.push(directGuestEmails.slice(i, i + 10));
    let cancelled = false;
    Promise.all(chunks.map((chunk) =>
      getDocs(query(collection(db, 'users'), where('email', 'in', chunk)))
        .then((snap) => {
          const byEmail = {};
          snap.docs.forEach((d) => {
            const data = d.data();
            const e = normalize(data.email);
            if (!e) return;
            byEmail[e] = {
              name: data.displayName || data.name || '',
              phone: data.phone || data.phoneNumber || ''
            };
          });
          return byEmail;
        })
        .catch(() => ({}))
    )).then((results) => {
      if (cancelled) return;
      const merged = {};
      results.forEach((r) => Object.assign(merged, r));
      setGuestContacts(merged);
    });
    return () => { cancelled = true; };
  }, [directGuestEmails]);

  // Contact info captured on creator-link leads (phone/name live on the lead).
  const leadContactByEmail = useMemo(() => {
    const map = {};
    joinedUsers.forEach((u) => {
      const e = normalize(u.email || u.userEmail || (u.linkData && u.linkData.email));
      if (!e) return;
      const name = u.name || (u.linkData && u.linkData.name) || '';
      const phone = u.phone || (u.linkData && u.linkData.phone) || '';
      const existing = map[e] || {};
      map[e] = {
        name: existing.name || name,
        phone: existing.phone || phone
      };
    });
    return map;
  }, [joinedUsers]);

  const contactByEmail = useMemo(
    () => ({ ...leadContactByEmail, ...guestContacts }),
    [leadContactByEmail, guestContacts]
  );

  const totals = useMemo(() => {
    let clicks = 0;
    creatorLinks.forEach((l) => { clicks += l.clicks || 0; });
    return {
      influencers: influencers.length,
      links: creatorLinks.length,
      clicks,
      usersJoined: joinedUsers.length,
      enquiries: influencerCards.reduce((sum, c) => sum + c.enquiries.length, 0)
    };
  }, [influencers, creatorLinks, joinedUsers, influencerCards]);

  const filteredCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return influencerCards;
    return influencerCards.filter(
      (c) =>
        c.email.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.links.some((l) => (l.tripName || '').toLowerCase().includes(q))
    );
  }, [influencerCards, searchQuery]);

  const visibleCards = useMemo(() => {
    if (isSuperAdmin) return influencerCards;
    if (isAuthorizedInfluencer) {
      const email = normalize(user?.email || '');
      return influencerCards.filter((c) => normalize(c.email) === email);
    }
    return [];
  }, [influencerCards, isSuperAdmin, isAuthorizedInfluencer, user]);

  const displayCards = isSuperAdmin ? filteredCards : visibleCards;

  const visibleTotals = useMemo(() => {
    if (isSuperAdmin) return totals;
    return {
      influencers: visibleCards.length,
      links: visibleCards.reduce((s, c) => s + c.links.length, 0),
      clicks: visibleCards.reduce((s, c) => s + c.totalClicks, 0),
      usersJoined: visibleCards.reduce((s, c) => s + c.usersJoined.length, 0),
      enquiries: visibleCards.reduce((s, c) => s + c.enquiries.length, 0)
    };
  }, [isSuperAdmin, totals, visibleCards]);

  const toggleCard = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleAddInfluencer = async () => {
    const value = emailInput.trim();
    if (!validateEmail(value)) {
      setError('Please enter a valid email address.');
      return;
    }
    const exists = influencers.some((i) => normalize(i.email || i.id) === normalize(value));
    if (exists) {
      setError('This email is already an authorized influencer.');
      return;
    }
    try {
      await setDoc(doc(db, 'influencers', value), {
        email: value,
        addedBy: 'super-admin',
        addedAt: new Date()
      });
      setEmailInput('');
      setError('');
    } catch (err) {
      console.error('Error adding influencer:', err);
      setError('Failed to add influencer: ' + err.message);
    }
  };

  const handleToggleTracking = async () => {
    if (!isSuperAdmin || togglingTracking) return;
    const next = !trackingEnabled;
    setTogglingTracking(true);
    try {
      await setDoc(
        doc(db, 'settings', 'influencerTracking'),
        {
          enabled: next,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || SUPER_ADMIN_EMAIL
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error updating access setting:', err);
      setError('Failed to update influencer access: ' + err.message);
    } finally {
      setTogglingTracking(false);
    }
  };

  const handleToggleInfluencerAccess = async (influencer) => {
    if (!isSuperAdmin || togglingAccessId) return;
    setTogglingAccessId(influencer.id);
    try {
      await updateDoc(doc(db, 'influencers', influencer.id), {
        accessEnabled: !influencer.accessEnabled,
        accessUpdatedAt: serverTimestamp(),
        accessUpdatedBy: user?.email || SUPER_ADMIN_EMAIL
      });
    } catch (err) {
      console.error('Error updating influencer access:', err);
      setError('Failed to update access for ' + influencer.email + ': ' + err.message);
    } finally {
      setTogglingAccessId(null);
    }
  };

  const handleRemoveInfluencer = async (email) => {
    if (!window.confirm(`Remove ${email} from influencers?`)) return;
    try {
      await deleteDoc(doc(db, 'influencers', email));
    } catch (err) {
      console.error('Error removing influencer:', err);
      setError('Failed to remove influencer: ' + err.message);
    }
  };

  const getFullLink = (link) => {
    const creatorName = link.creatorName || normalize(link.influencerEmail).split('@')[0] || 'creator';
    const tripName = link.tripName || 'trip';
    return `https://www.roundtown.in/creator/${slugify(creatorName)}/${slugify(tripName)}/${link.token}`;
  };

  const getRedirectUrl = (link) => {
    if (link.customUrl) return link.customUrl;
    if (link.tripId) return `https://www.roundtown.in/?itinerary=${link.tripId}`;
    return null;
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleResetClicks = async (linkId) => {
    if (!window.confirm('Reset click count to 0?')) return;
    try {
      await updateDoc(doc(db, 'creatorLinks', linkId), { clicks: 0 });
    } catch (err) {
      console.error('Error resetting clicks:', err);
      setError('Failed to reset clicks: ' + err.message);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Delete this creator link?')) return;
    try {
      await deleteDoc(doc(db, 'creatorLinks', linkId));
    } catch (err) {
      console.error('Error deleting link:', err);
      setError('Failed to delete link: ' + err.message);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setRefreshTick((t) => t + 1);
  };

  if (!authChecked || loading || !settingsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading influencer tracking data...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm border border-purple-100">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">Influencer Tracking</h1>
          <p className="text-sm text-gray-500 text-center mb-6">
            Sign in with Google to view your creator links and enquiries. Super admin sees all influencers.
          </p>
          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
              <XCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">{error}</div>
            </div>
          )}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
          >
            <LogIn size={20} className="text-blue-500" />
            Continue with Google
          </button>
        </div>
      </div>
    );
  }

  if (!trackingEnabled && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm border border-purple-100 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Temporarily Unavailable</h1>
          <p className="text-sm text-gray-500 mb-4">
            Influencer tracking has been turned off by the admin. Please try again later.
          </p>
          {user ? (
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 p-3 bg-gray-700 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          ) : (
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
            >
              <LogIn size={20} className="text-blue-500" />
              Continue with Google
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!canAccess) {
    if (!influencersLoaded) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      );
    }
    const isListed = authorizedEmails.has(normalize(user.email || ''));
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm border border-red-100">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 text-center mb-2">
            {isListed ? 'Access Turned Off' : 'Unauthorized Access'}
          </h1>
          <p className="text-sm text-gray-500 text-center mb-4">
            Signed in as <strong className="text-gray-700">{user.email}</strong>.{' '}
            {isListed
              ? 'The super admin has turned off your portal access.'
              : 'This email is not an authorized influencer.'}
          </p>
          <p className="text-xs text-gray-400 text-center mb-6">
            {isListed
              ? 'Ask the super admin to turn your access back on.'
              : 'Ask the super admin to add your email under "Manage Influencers" to access your creator link data.'}
          </p>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 p-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-purple-100">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <button
              onClick={() => navigate(isSuperAdmin ? '/super-admin' : '/hostProfile')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 shadow-lg"
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
                  Influencer Tracking
                </h1>
                <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                  <span>{isSuperAdmin ? 'Clicks, creator links & property enquiries per influencer' : `${user.displayName || user.email} — clicks, creator links & enquiries`}</span>
                  <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    <CheckCircle size={12} /> Live
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <span className="text-xs text-gray-500">
                  Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium text-sm hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
              >
                <RefreshCw size={16} className="inline-block mr-2" />
                Refresh
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
                <span className="text-sm font-medium hidden sm:inline">{isSuperAdmin ? 'Sign Out' : user.displayName || 'Sign Out'}</span>
              </button>
            </div>
          </div>

          {isSuperAdmin && (
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-purple-100 bg-purple-50/60">
              <div>
                <p className="text-sm font-semibold text-gray-800">Influencer Portal Access</p>
                <p className="text-xs text-gray-500">
                  {trackingEnabled
                    ? 'On — authorized influencers can sign in and view their links and enquiries.'
                    : 'Off — influencers cannot sign in. Only you can view this page.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleToggleTracking}
                disabled={togglingTracking}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-60 ${
                  trackingEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${trackingEnabled ? 'bg-green-200' : 'bg-gray-300'}`} />
                {trackingEnabled ? 'On' : 'Off'}
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
              <XCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">{error}</div>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 font-medium">Close</button>
            </div>
          )}

          {/* Stats */}
          {isSuperAdmin && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">Influencers</p>
              <p className="text-2xl font-bold text-purple-800 mt-1">{formatCount(visibleTotals.influencers)}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs text-blue-700 font-medium uppercase tracking-wide">Creator Links</p>
              <p className="text-2xl font-bold text-blue-800 mt-1">{formatCount(visibleTotals.links)}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-xs text-green-700 font-medium uppercase tracking-wide">Total Clicks</p>
              <p className="text-2xl font-bold text-green-800 mt-1">{formatCount(visibleTotals.clicks)}</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Users Joined</p>
              <p className="text-2xl font-bold text-amber-800 mt-1">{formatCount(visibleTotals.usersJoined)}</p>
            </div>
            <div className="bg-teal-50 rounded-xl p-4">
              <p className="text-xs text-teal-700 font-medium uppercase tracking-wide">Property Enquiries</p>
              <p className="text-2xl font-bold text-teal-800 mt-1">{formatCount(visibleTotals.enquiries)}</p>
            </div>
          </div>
          )}
        </div>

        {/* Manage Influencers */}
        {isSuperAdmin && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-purple-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <UserPlus size={20} className="text-purple-600" />
            Manage Influencers
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddInfluencer(); }}
              placeholder="Enter influencer email"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={handleAddInfluencer}
              className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
            >
              Add
            </button>
          </div>

          <h4 className="text-sm font-semibold text-gray-700 mb-2">Authorized Influencers ({influencers.length})</h4>
          {influencers.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">No influencers added yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {influencers.map((inf) => {
                const email = inf.email || inf.id;
                return (
                  <div key={inf.id} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full pl-4 pr-2 py-1.5">
                    <span className="text-sm text-purple-800">{email}</span>
                    <button
                      onClick={() => handleRemoveInfluencer(email)}
                      className="p-1 bg-purple-200 text-purple-700 rounded-full hover:bg-red-200 hover:text-red-700 transition-colors"
                      title={`Remove ${email}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Search + Enquiry filter */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          {isSuperAdmin && (
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search influencer email or name..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Enquiries:</span>
            <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
              {ENQUIRY_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setEnquiryFilter(f.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    enquiryFilter === f.value
                      ? 'bg-white text-purple-700 shadow'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Influencer cards */}
        {displayCards.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">{isSuperAdmin ? 'No Influencer Data' : 'No Creator Links Yet'}</h3>
            <p className="text-gray-600">
              {isSuperAdmin
                ? 'Add an influencer above to start tracking their creator links and leads.'
                : 'No creator links have been generated for your account yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {displayCards.map((card) => {
              const isCollapsed = collapsedIds.has(card.id);
              return (
                <div key={card.id} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100">
                  {/* Card header */}
                  <div className="flex items-center gap-3 p-5 hover:bg-purple-50/50 transition-colors">
                  <button
                    onClick={() => toggleCard(card.id)}
                    className="flex-1 min-w-0 flex items-center justify-between gap-4 text-left"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {card.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-800 truncate">{card.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{card.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="hidden sm:flex items-center gap-4 text-center">
                        {isSuperAdmin && (
                        <div>
                          <p className="text-xs text-gray-500">Clicks</p>
                          <p className="font-bold text-green-600 whitespace-nowrap">{formatCount(card.totalClicks)}</p>
                        </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500">Links</p>
                          <p className="font-bold text-blue-600 whitespace-nowrap">{formatCount(card.links.length)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Enquiries</p>
                          <p className="font-bold text-purple-600 whitespace-nowrap">{formatCount(card.totalLeads)}</p>
                        </div>
                      </div>
                      {isCollapsed ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronUp size={20} className="text-gray-400" />}
                    </div>
                  </button>
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => handleToggleInfluencerAccess(card)}
                      disabled={togglingAccessId === card.id}
                      title={card.accessEnabled ? 'Turn off portal access for this influencer' : 'Turn on portal access for this influencer'}
                      className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors disabled:opacity-60 ${
                        card.accessEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${card.accessEnabled ? 'bg-green-200' : 'bg-gray-300'}`} />
                      Access {card.accessEnabled ? 'On' : 'Off'}
                    </button>
                  )}
                  </div>

                  {!isCollapsed && (
                    <div className="px-5 pb-5 space-y-5">
                      {/* Creator links */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Link2 size={16} className="text-purple-500" />
                          Creator Links ({card.links.length})
                        </h4>
                        {card.links.length === 0 ? (
                          <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No creator links generated yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {card.links.map((link) => {
                              const fullLink = getFullLink(link);
                              const redirectUrl = getRedirectUrl(link);
                              const users = usersByToken.get(link.token) || [];
                              const periodUsers = users.filter((u) => isWithinPeriod(u.createdAt, enquiryFilter));
                              const stayGroups = groupUsersByStay(periodUsers, link.tripName);
                              return (
                                <div key={link.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-800 truncate">
                                      {link.tripName || 'Untitled Trip'}
                                    </span>
                                    {isSuperAdmin && (
                                    <span className="text-sm font-bold text-purple-600 flex items-center gap-1 whitespace-nowrap">
                                      <MousePointerClick size={14} /> {formatCount(link.clicks ?? 0)} clicks
                                    </span>
                                    )}
                                  </div>

                                  {isSuperAdmin && redirectUrl && (
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="text-xs text-gray-500 flex-shrink-0">Redirects to:</span>
                                      <p className="text-xs text-green-600 truncate flex-1">{redirectUrl}</p>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-1 mb-2">
                                    <p className="text-xs text-blue-500 truncate flex-1">{fullLink}</p>
                                    <button
                                      onClick={() => handleCopy(fullLink, 'copy-' + link.id)}
                                      className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex-shrink-0 font-medium inline-flex items-center gap-1"
                                    >
                                      <ClipboardCopy size={12} />
                                      {copiedId === 'copy-' + link.id ? 'Copied!' : 'Copy'}
                                    </button>
                                  </div>

                                  {isSuperAdmin && (
                                  <div className="flex flex-wrap gap-1">
                                    <button
                                      onClick={() => navigate(`/admin/creator-users/${link.token}`)}
                                      className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium inline-flex items-center gap-1"
                                    >
                                      <Users size={12} /> View Users
                                    </button>
                                    <button
                                      onClick={() => handleResetClicks(link.id)}
                                      className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 font-medium inline-flex items-center gap-1"
                                    >
                                      <RotateCcw size={12} /> Reset Clicks
                                    </button>
                                    <button
                                      onClick={() => handleDeleteLink(link.id)}
                                      className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 font-medium inline-flex items-center gap-1"
                                    >
                                      <Trash2 size={12} /> Delete
                                    </button>
                                  </div>
                                  )}

                                  {stayGroups.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-dashed border-gray-300">
                                      <p className="text-xs font-semibold text-purple-700 flex items-center gap-1 mb-1">
                                        <Users size={12} /> Enquiries ({periodUsers.length})
                                        <span className="text-gray-400 font-normal">· {periodLabel(enquiryFilter)}</span>
                                      </p>
                                      <div className="space-y-1.5">
                                        {stayGroups.map((g) => (
                                          <div key={g.stay} className="text-xs bg-white rounded-lg p-2 border border-gray-200 flex items-center justify-between gap-2">
                                            <span className="flex items-center gap-1 min-w-0">
                                              <Building2 size={12} className="text-purple-500 flex-shrink-0" />
                                              <span className="font-medium text-gray-800 truncate">{g.stay}</span>
                                            </span>
                                            <span className="bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-semibold flex-shrink-0">
                                              {g.count} {g.count === 1 ? 'enquiry' : 'enquiries'}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Property enquiries */}
                      {isSuperAdmin && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Building2 size={16} className="text-teal-500" />
                          Property Enquiries ({card.enquiries.length})
                          <span className="text-xs text-gray-400 font-normal">via creator tokens</span>
                        </h4>
                        {card.enquiries.length === 0 ? (
                          <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No property enquiries yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {card.enquiries.map((enq) => (
                              <div key={enq.id} className="text-sm bg-teal-50 border border-teal-100 rounded-lg p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Building2 size={14} className="text-teal-600 flex-shrink-0" />
                                    <span className="font-medium text-gray-800 truncate">{enq.property}</span>
                                  </div>
                                  <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                                    <Calendar size={12} /> {formatTimestamp(enq.createdAt)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Unassigned links */}
        {isSuperAdmin && unassignedLinks.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl p-6 border border-amber-100">
            <div className="flex items-center gap-2 mb-3">
              <Link2 size={18} className="text-amber-600" />
              <h3 className="text-lg font-bold text-gray-800">Unassigned Creator Links ({unassignedLinks.length})</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              These links belong to emails that are not in the authorized influencer list. Add the owner email under Manage Influencers to track them, or delete them here.
            </p>
            <div className="space-y-2">
              {unassignedLinks.map((link) => {
                const fullLink = getFullLink(link);
                return (
                  <div key={link.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{link.influencerEmail || 'No email'}</p>
                      <p className="text-xs text-blue-500 truncate">{fullLink}</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600 flex items-center gap-1 whitespace-nowrap">
                      <MousePointerClick size={14} /> {formatCount(link.clicks ?? 0)} clicks
                    </span>
                    <button
                      onClick={() => handleDeleteLink(link.id)}
                      className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 font-medium inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Direct bookings (no creator token) */}
        {isSuperAdmin && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl p-6 border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={18} className="text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-800">Direct Bookings ({directBookings.length})</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Resort enquiries made directly without a creator token.
              <span className="text-gray-400"> · {periodLabel(enquiryFilter)}</span>
            </p>
            {directBookings.length === 0 ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">No direct bookings in this period.</p>
            ) : (
              <div className="space-y-2">
                {directBookings.map((b) => {
                  const contact = contactByEmail[normalize(b.guestEmail)] || {};
                  const customerName = contact.name || '';
                  const phone = contact.phone || '';
                  return (
                  <div key={b.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 size={14} className="text-indigo-500 flex-shrink-0" />
                        <span className="font-medium text-gray-800 truncate">{b.property}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          b.status === 'confirmed' ? 'bg-green-100 text-green-700'
                            : b.status === 'cancelled' ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                        <Calendar size={12} /> {formatTimestamp(b.createdAt)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                      {customerName ? (
                        <span className="flex items-center gap-1 min-w-0">
                          <UserRound size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate">{customerName}</span>
                        </span>
                      ) : null}
                      {b.guestEmail ? (
                        <span className="flex items-center gap-1 min-w-0">
                          <Mail size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate">{b.guestEmail}</span>
                        </span>
                      ) : null}
                      {phone ? (
                        <span className="flex items-center gap-1">
                          <Phone size={12} className="text-gray-400 flex-shrink-0" />
                          <span>{phone}</span>
                        </span>
                      ) : null}
                      {b.guests ? (
                        <span className="flex items-center gap-1">
                          <Users size={12} className="text-gray-400 flex-shrink-0" />
                          <span>{b.guests} person{b.guests === 1 ? '' : 's'}{b.rooms ? `, ${b.rooms} room${b.rooms === 1 ? '' : 's'}` : ''}</span>
                        </span>
                      ) : null}
                      {b.totalPrice ? <span className="font-semibold text-gray-700">₹{formatCount(b.totalPrice)}</span> : null}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfluencerCommission;